import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import SideNavbar from '../../../components/navbar/SideNavbar';
import { getInventorySettings, updateInventorySetting } from '../../../api/settings';
import useAuthStore from '../../../datastore/useAuth';
import { isAdmin, isGlobalAdmin, isSiteAdmin, isStaff } from '../../../common/typeAssertionFunctions';
import useStations from '../../../datastore/useStations';
import useSites from '../../../datastore/useSites';
import useAdminSiteContext from '../../../hooks/useAdminSiteContext';
import { Staff } from '../../../common/types';
import { Role } from '../../../common/enums';
import { FormControlLabel, Switch } from '@mui/material';
import ConfirmModal from '../../../components/modal/ConfirmModal';
import NotificationMenu from '../../../components/notifications/NotificationMenu';

export default function StaffLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const theme = useTheme();
  const { currentUser } = useAuthStore()
  const { fetchStations } = useStations()
  const { sites } = useSites()
  const { adminSiteId, setAdminSiteId } = useAdminSiteContext()
  const [isInventoryEnabled, setIsInventoryEnabled] = useState<boolean>(true);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [pendingInventoryState, setPendingInventoryState] = useState<boolean | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const showSiteSelector = isGlobalAdmin(currentUser);

  const handleInventoryToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPendingInventoryState(e.target.checked);
    setShowConfirmModal(true);
  };

  const handleConfirmToggle = async () => {
    if (pendingInventoryState === null) return;
    setIsUpdating(true);
    try {
      await updateInventorySetting({ slug: 'ByPassInventory', slugValue: pendingInventoryState });
      window.location.reload();
    } catch (error) {
      console.error('Failed to update inventory setting:', error);
    } finally {
      setIsUpdating(false);
      setShowConfirmModal(false);
      setPendingInventoryState(null);
    }
  };

  useEffect(() => {
    fetchStations()
    const unsubscribe = useStations.subscribe((state) => {
      if (state.stations.length > 0) {
        const staff = useAuthStore.getState().currentUser as Staff
        if (staff?.stationId && !staff?.stationName) {
          const station = state.stations.find((station) => station.id === staff.stationId)
          if (station) {
            staff.stationName = station.name
            useAuthStore.setState({ currentUser: { ...staff } })
          }
        }
      }
    })
    return () => unsubscribe()
  }, [])

  // Auto-select first site if admin has no site selected
  useEffect(() => {
    if (showSiteSelector && !adminSiteId && sites.length > 0) {
      setAdminSiteId(sites[0].id);
    }
  }, [showSiteSelector, adminSiteId, sites, setAdminSiteId])

  // Fetch settings when adminSiteId changes
  useEffect(() => {
    if (!adminSiteId) return;
    const fetchSettings = async () => {
      try {
        const response = await getInventorySettings(`slug=ByPassInventory`);
        if (response.data) {
          setIsInventoryEnabled(response.data.slugValue === true);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, [adminSiteId])

  // Automatically select the assigned site if user is a site admin or general staff
  useEffect(() => {
    if (isSiteAdmin(currentUser) || (currentUser && currentUser.roleName === Role.General)) {
      if (currentUser.siteId && currentUser.siteId !== adminSiteId) {
        setAdminSiteId(currentUser.siteId);
      }
    }
  }, [currentUser, adminSiteId, setAdminSiteId]);

  return (
    <Stack flexDirection="row" sx={{ height: '100vh', overflow: 'hidden' }}>
      <SideNavbar />
      <Box
        sx={{
          flexGrow: 1,
          borderLeft: `1px solid ${theme.palette.common.black}`,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {showSiteSelector && (
          <Box
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: theme.palette.grey[100],
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexShrink: 0,
            }}
          >
            {sites.length > 1 && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Viewing Site:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="admin-site-select-label">Select Site</InputLabel>
                  <Select
                    labelId="admin-site-select-label"
                    id="admin-site-select"
                    value={adminSiteId ?? ''}
                    label="Select Site"
                    onChange={(e) => setAdminSiteId(Number(e.target.value))}
                  >
                    {sites.map((site) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {isAdmin(currentUser) && <NotificationMenu />}
              <FormControlLabel
                control={
                  <Switch
                    checked={isInventoryEnabled}
                    onChange={handleInventoryToggle}
                    disabled={isUpdating}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Inventory  {isInventoryEnabled ? 'Bypass' : 'Active'}
                  </Typography>
                }
              />
            </Box>
          </Box>
        )}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
          {
            !isStaff(currentUser) ? (
              <Box sx={{ p: 3 }}>
                <Typography>You do not have access to this page</Typography>
              </Box>
            ) : (
              children
            )
          }
        </Box>
      </Box>
      {showConfirmModal && pendingInventoryState !== null && (
        <ConfirmModal
          title={`Confirm ${pendingInventoryState ? 'ByPass Inventory' : 'Inventory Active'}`}
          content={`Are you sure you want to ${pendingInventoryState ? 'bypass the inventory' : 'activate the inventory'}?`}
          onClose={() => {
            setShowConfirmModal(false);
            setPendingInventoryState(null);
          }}
          onConfirm={handleConfirmToggle}
        />
      )}
    </Stack>
  );
}